import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { type CartItem } from './mobile-cart.service';
import { MobileSessionService } from './mobile-session.service';
import { environment } from '../environments/environment';

type CreateOrderLineInput = {
  product_id: string;
  quantity: number;
  currency_code: string;
};

type CreateOrderInput = {
  notes: string;
  line_items: CreateOrderLineInput[];
};

type DryStackResponse<T> = {
  success?: boolean;
  messages?: string[];
  data?: T;
};

type PurchaseOrderResponse = {
  id?: string;
};

@Injectable({ providedIn: 'root' })
export class MobileOrdersService {
  private readonly http = inject(HttpClient);
  private readonly sessionService = inject(MobileSessionService);
  private readonly apiBaseURL = this.resolveAPIBaseURL(environment.apiBaseUrl);
  private readonly apiURL = `${this.apiBaseURL}/api/v1/bg-imports/customer/purchase-orders`;

  public async placeOrder(cartRows: CartItem[], invoiceRef: string): Promise<{ ok: boolean; message: string }> {
    const session = this.sessionService.session();
    const token = String(session?.token || '').trim();
    if (!token) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }

    const lineItems: CreateOrderLineInput[] = cartRows
      .filter((row) => String(row.productID || '').trim() !== '')
      .map((row) => ({
        product_id: String(row.productID).trim(),
        quantity: Math.max(1, Math.floor(Number(row.quantity) || 1)),
        currency_code: 'EUR',
      }));

    if (lineItems.length === 0) {
      return { ok: false, message: 'Your cart is empty.' };
    }

    const payload: CreateOrderInput = {
      notes: `Placed from website dashboard cart (${invoiceRef})`,
      line_items: lineItems,
    };

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'x-request-id': this.requestID(),
    });

    try {
      const response = await firstValueFrom(
        this.http.post<DryStackResponse<PurchaseOrderResponse>>(this.apiURL, payload, { headers }),
      );

      if (response?.success) {
        const orderID = String(response?.data?.id || '').trim();
        if (orderID) {
          return { ok: true, message: `Order placed successfully. PO ID: ${orderID}` };
        }
        return { ok: true, message: 'Order placed successfully.' };
      }

      const fallbackMessage = this.messagesToText(response?.messages);
      return { ok: false, message: fallbackMessage || 'Failed to place order.' };
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;
      const status = Number(httpError?.status || 0);
      const payloadError = httpError?.error as DryStackResponse<unknown> | { error?: string; message?: string };

      const message =
        this.messagesToText((payloadError as DryStackResponse<unknown>)?.messages) ||
        String((payloadError as { error?: string })?.error || '').trim() ||
        String((payloadError as { message?: string })?.message || '').trim();

      if (status === 401) {
        return { ok: false, message: message || 'Unauthorized. Please sign in again.' };
      }

      if (status === 0) {
        return { ok: false, message: 'Unable to reach order server. Please try again.' };
      }

      return { ok: false, message: message || 'Failed to place order.' };
    }
  }

  private messagesToText(messages?: string[]): string {
    if (!messages || !messages.length) {
      return '';
    }

    return messages.map((message) => String(message || '').trim()).filter(Boolean).join(' ');
  }

  private requestID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `req-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }

  private resolveAPIBaseURL(url: string): string {
    return String(url || '').replace(/\/+$/, '');
  }
}
